/**
 * Doküman kütüphanesinin yalnız web ofisteki ekranlarının metinleri (M8a3 Task 17): onay akışı,
 * yeni doküman ve revizyon çekmecesi, Form Categories, dağıtım panosu ve panodaki bekleyen
 * onaylar. `documents.ts` bu nesneleri `documents` altına katar.
 */

export const documentsOfficeEn = {
  storage: {
    title: 'Document files',
    quota: 'Storage quota',
    used: '{{used}} of {{quota}}',
    disk: 'Ship server disk',
    free: '{{free}} free',
    levels: { ok: 'OK', low: 'Low', critical: 'Critical' },
    waiting: 'Waiting',
    failed: 'Failed',
    bulk: 'Bulk downloads',
    running: 'Running',
    paused: 'Paused until {{date}} by {{by}}',
    pause: 'Pause bulk downloads',
    pauseFor_one: 'For {{count}} day',
    pauseFor_other: 'For {{count}} days',
    resume: 'Resume downloads',
    hint: 'Forms and procedures download first; large manuals wait for the bulk window.',
    import: 'Import from folder',
    importTitle: 'Select the folder with the document files (USB or backup disk)',
    importHint: 'Files are matched by content; names do not matter.',
    progress: {
      listing: 'Listing files: {{files}}',
      scanning: 'Checking files: {{files}} of {{total}}',
      uploading: 'Copying files: {{done}} of {{total}}',
      starting: 'Opening the folder…',
    },
    importDone_one: '{{count}} file imported, {{failed}} failed',
    importDone_other: '{{count}} files imported, {{failed}} failed',
    importNothing: 'No missing files were found in the folder.',
    importFailed: 'Import failed.',
  },
  read: {
    title: 'Read and understood',
    body: '{{revision}} requires your personal read confirmation.',
    confirm: 'Confirm read',
    dialogTitle: 'Confirm read',
    dialogBody: 'I have read and understood {{document}} {{revision}}.',
    password: 'Password',
    passwordHint: 'Enter your password to sign.',
    cancel: 'Cancel',
    done: 'Read confirmation recorded.',
  },
  vesselFiles: {
    loading: 'Loading the vessel’s file settings…',
    loadError: 'The vessel’s file settings could not be read.',
    readOnly: 'Changing these settings needs the Settings permission.',
    open: 'Files of {{vessel}}',
    title: 'Files of {{vessel}}',
    used: '{{used}} of {{quota}}',
    deferred: '{{count}} deferred',
    failed: '{{count}} failed',
    paused: 'Paused on board',
    disk: { ok: 'Disk OK', low: 'Disk low', critical: 'Disk critical' },
    tabs: { settings: 'Download settings', export: 'Export for USB' },
    quota: 'Storage quota (GB)',
    threshold: 'Immediate download up to (MB)',
    thresholdHint: 'Larger files wait for the bulk window, except forms, procedures and updates.',
    mode: 'Bulk downloads',
    modes: { window: 'In the time window', always: 'Always', manual: 'Only on request' },
    windowStart: 'Window start',
    windowEnd: 'Window end',
    windowHint: 'Vessel local time.',
    cap: 'Monthly bulk limit (GB)',
    capHint: '0 turns the limit off.',
    rate: 'Speed limit (kbps)',
    rateHint: '0 means no limit.',
    save: 'Save',
    cancel: 'Cancel',
    saved: 'Download settings saved.',
    invalid: 'Check the values: quota and threshold above 0, window start and end different.',
    exportBody:
      'Copies the files that {{vessel}} has not verified yet to a folder (e.g. a USB disk). On board, use Settings › Document files › Import from folder.',
    exportCount_one: '{{count}} file',
    exportCount_other: '{{count}} files',
    exportNone: 'The vessel has every file. Nothing to export.',
    exportStart: 'Choose folder and export',
    exportProgress: '{{done}} of {{total}} files',
    exportDone: 'Export finished.',
    unsupported: 'Needs Chrome or Edge (File System Access).',
  },
  page: { first: 'First page', previous: 'Previous page', next: 'Next page', last: 'Last page' },
  filter: {
    label: 'Show',
    all: 'All',
    pending: 'Pending approval ({{count}})',
    pendingEmpty: 'No document awaits approval.',
  },
  workflow: {
    title: 'Approval workflow',
    revision: 'Rev. {{revision}}',
    preparedBy: 'Prepared by {{name}} on {{date}}',
    events: {
      submitted: 'Submitted by {{name}} on {{date}}',
      returned: 'Returned by {{name}} on {{date}}',
      approved: 'Approved by {{name}} on {{date}}',
      published: 'Published by {{name}} on {{date}}',
      rescheduled: 'Rescheduled by {{name}} on {{date}}',
    },
    reason: 'Reason: {{reason}}',
    waiting: {
      draft: 'Draft – not yet submitted for approval.',
      pending_approval: 'Awaiting a decision by an approver.',
      approved: 'Approved – ready to publish.',
      scheduled: 'Takes effect on {{date}}.',
      published: 'Current revision.',
    },
    // Geminin eski belgesinde (ADR 0081): onay ve yayın artık şirketin
    waitingVessel: 'Only the company publishes documents now. This revision will not be published.',
    actions: {
      submit: 'Submit for approval',
      return: 'Return',
      approve: 'Approve',
      // Ortağın geri bildirimi 2026-10-18 (ADR 0081): yayının nereye gittiği adında
      publish: 'Publish to vessels',
      withdraw: 'Withdraw',
      discard: 'Discard',
      reschedule: 'Reschedule',
      cancel: 'Cancel publication',
    },
    dialog: {
      titles: {
        submit: 'Submit for approval',
        return: 'Return to the uploader',
        approve: 'Approve revision',
        publish: 'Publish revision',
        withdraw: 'Withdraw revision',
        discard: 'Discard draft',
        reschedule: 'Reschedule revision',
        cancel: 'Cancel publication',
      },
      bodies: {
        submit:
          '{{document}} Rev. {{revision}} goes to the approver. The file can no longer be changed.',
        return:
          '{{document}} Rev. {{revision}} goes back to draft. The uploader discards it and uploads a corrected draft.',
        approve: 'I approve {{document}} Rev. {{revision}} with the content shown.',
        publish:
          '{{document}} Rev. {{revision}} becomes current on all vessels in scope. The previous revision is superseded.',
        withdraw:
          '{{document}} Rev. {{revision}} is withdrawn. Vessels must remove printed copies and acknowledge.',
        discard: 'The draft {{document}} Rev. {{revision}} is closed. It stays in the history.',
        reschedule: '{{document}} Rev. {{revision}} takes effect on the new date instead.',
        cancel:
          'The scheduled publication of {{document}} Rev. {{revision}} is cancelled. The current revision stays in force.',
      },
      withdrawWarning:
        'Vessels in scope will be left without a current revision. To correct a wrong revision, publish a corrected one instead.',
      withdrawVessels_one:
        '{{count}} vessel will be left without a current revision. To correct a wrong revision, publish a corrected one instead.',
      withdrawVessels_other:
        '{{count}} vessels will be left without a current revision. To correct a wrong revision, publish a corrected one instead.',
      reason: 'Reason',
      reasonHint: 'Required. Recorded in the history.',
      effectiveOn: 'Effective date',
      effectiveHint:
        'Leave empty to make it current now. Vessel local date; earliest {{min}}, latest {{max}}.',
      password: 'Password',
      passwordHint: 'Enter your password to sign.',
      cancel: 'Cancel',
    },
    done: {
      submit: 'Rev. {{revision}} submitted for approval.',
      return: 'Rev. {{revision}} returned to the uploader.',
      approve: 'Rev. {{revision}} approved.',
      publish: 'Rev. {{revision}} published.',
      withdraw: 'Rev. {{revision}} withdrawn.',
      discard: 'Draft Rev. {{revision}} discarded.',
      reschedule: 'Rev. {{revision}} rescheduled.',
      cancel: 'Publication of Rev. {{revision}} cancelled.',
    },
  },
  drawer: {
    newTitle: 'New Document',
    fillHint:
      'This adds a new form to the library; it does not fill one in. To fill in a form, close this panel, select the form and choose Fill in.',
    revisionTitle: 'New revision',
    category: 'Category',
    docNo: 'Document No',
    title: 'Title',
    kind: 'Type',
    description: 'Description',
    revisionNo: 'Revision No',
    revisionNoHint: 'Your company revision number, e.g. 2.0.',
    revisionPickHint:
      'Choose the revision the form already has on paper: Original (Rev. 0), Rev. 1, Rev. 2… Later revisions continue from it.',
    changeSummary: 'Change Summary',
    changeSummaryHint: 'What changed in this revision. Shown on board.',
    file: 'Document file',
    fileHint: 'Word, Excel, PDF, JPG or PNG; up to 1 GB.',
    drop: 'Drop the file here or',
    choose: 'Choose file',
    replace: 'Replace file',
    attachments: 'Attachments',
    attachmentsHint: 'Optional supporting files.',
    // Baskı PDF'i (ADR 0028 #34(1); ADR 0055 §5)
    printCopy: 'Print copy (PDF)',
    printCopyHint:
      'Optional. Save the Word or Excel form as PDF and add it here: Print and Print Form print this PDF exactly, instead of an approximate rendering.',
    addAttachments: 'Add files',
    remove: 'Remove {{name}}',
    scope: 'Distribution',
    scopeHint:
      'All personnel of the vessels in the distribution can see this document. For a vessel’s own manual, such as its stability booklet, choose Selected vessels.',
    scopeChangeWarning:
      'The distribution belongs to the document: the change applies now to the current revision, not when this new revision is published. Vessels taken out of the distribution stop seeing the document at their next sync. Returning or discarding the draft does not undo it.',
    scopeChangeConfirm: 'I understand that the distribution changes now.',
    vessels: 'Vessels',
    vesselsPlaceholder: 'Select vessels',
    save: 'Save draft',
    cancel: 'Cancel',
    uploading: 'Uploading {{name}}',
    saving: 'Saving the draft…',
    saved: 'Draft Rev. {{revision}} saved. Submit it for approval when ready.',
    resume: 'The upload was interrupted. Save again to continue where it stopped.',
    locked:
      'The draft revision is already saved with this file, number and summary and with the attachments marked as added; they can no longer change. Save again to finish the remaining steps.',
    attached: 'Added to the revision',
    large:
      'Large file ({{size}}). Consider splitting it into sections; on board it waits for the night window and takes about {{hours}} h at 1 Mbps.',
  },
  // Form onay zinciri (ADR 0071 §2)
  chain: {
    title: 'Approval chain',
    hint: 'Who prepares the record and who approves it, in order. Changes apply to records submitted after saving.',
    default: 'Default rules (approval matrix)',
    custom: 'Custom chain',
    preparers: 'Prepared by',
    preparersHint: '1 or 2 ranks; they prepare the form together. One of them submits it.',
    review: 'Review {{step}}',
    removeReview: 'Remove review {{step}}',
    addReview: 'Add review step',
    master: 'Master approval',
    masterHint: 'The master approves after the reviews.',
    quorumAll: 'All sign',
    quorumAny: 'Any one',
    summary: 'Chain: {{chain}}',
    defaultSummary: 'Default rules: the Master approves on board.',
    // ISM formu ofis onayına gitmez (sahip kararı 2026-10-17; ADR 0073)
    officeNote: 'The office sees the records. ISM forms do not go to the office for approval.',
    edit: 'Edit approval chain',
    dialogTitle: 'Approval chain · {{document}}',
    appliesNext:
      'The new chain applies to records submitted from now on. Records already waiting for approval keep the chain they were submitted with.',
    save: 'Save chain',
    saved: 'Approval chain saved.',
  },
  validation: {
    REQUIRED: 'Required.',
    TOO_LONG: 'Too long.',
    INVALID: 'Invalid value.',
    SCOPE_VESSELS: 'Select at least one vessel.',
    DUPLICATE: 'Duplicate selection.',
    READ_ROLES: 'Select at least one role.',
    FILE_REQUIRED: 'Choose the document file.',
    PRINT_COPY_MUST_BE_PDF: 'The print copy must be a PDF file.',
    PRINT_COPY_NOT_APPLICABLE: 'A print copy is added only to a Word or Excel document file.',
    CONTROL_CHARACTER: 'Remove the line breaks and control characters.',
    // Onay zinciri (ADR 0071 §2)
    CHAIN_PREPARER_REQUIRED: 'Select at least one preparing rank.',
    CHAIN_STEP_EMPTY: 'Select a rank for every step.',
    INVALID_APPROVAL_CHAIN: 'The approval chain is not valid.',
    CHAIN_ROLE_REPEATED: 'A rank can appear only once in the chain.',
    UNKNOWN_ROLE: 'A selected role no longer exists.',
    FORM_OFFICE_STEP_NOT_ALLOWED: 'ISM forms do not go to the office for approval.',
    CHAIN_ONLY_FOR_FORMS: 'An approval chain is set only on forms.',
    SCOPE_CHANGE_UNCONFIRMED: 'Confirm that the distribution changes now.',
    // Dosya türü seçimde (ADR 0055 §2)
    OLD_OFFICE_FORMAT:
      'Word 97–2003 (.doc) and Excel 97–2003 (.xls) files are not accepted. Open the file in Word or Excel and save it as .docx or .xlsx.',
    MACRO_FILE:
      'Files with macros (.docm, .xlsm) are not accepted. Save the file without macros as .docx or .xlsx.',
    UNSUPPORTED_FILE_TYPE:
      'This file type is not accepted. Use Word (.docx), Excel (.xlsx), PDF, JPG or PNG.',
  },
  categories: {
    title: 'Form Categories',
    subtitle: 'The folder tree of the ISM Forms library.',
    caption: 'Category tree',
    documents_one: '{{count}} document',
    documents_other: '{{count}} documents',
    readOnly:
      'The category tree comes from Felixmate (vessel template). Documents are uploaded under these categories.',
    empty: 'No categories yet.',
    retired: 'Retired folders',
    retiredHint:
      'These folders were retired. They are read-only and stay visible because they still hold documents.',
    other: 'Other company documents',
    otherHint: 'Company documents whose folder is not in this vessel’s tree.',
  },
  folders: {
    title: 'Form Categories',
    subtitle:
      'The folder tree of each vessel. A change applies to this vessel, selected vessels or all vessels.',
    manualsTitle: 'Manuals – Manage folders',
    manualsCrumb: 'Manage folders',
    manualsSubtitle:
      'The Manuals folder tree of each vessel. A change applies to this vessel, selected vessels or all vessels.',
    manage: 'Manage folders',
    back: 'Back to {{section}}',
    library: 'Library',
    company: 'Company library (template mirror)',
    companyHint:
      'The company library mirrors the Felixmate template and is read-only. Vessel trees are edited per vessel.',
    template: 'Template: {{name}} v{{version}}',
    noTemplate: 'No document template has been applied to this vessel yet.',
    readOnly:
      'You can view the folder tree. Editing needs the “Document tree” permission (documents.tree).',
    caption: 'Folder tree',
    retiredCaption: 'Retired folders',
    retiredHint:
      'Retired folders are read-only. Documents stay where they are; on board, retired folders that hold documents appear under “Retired folders”.',
    empty: 'This vessel has no folders in this section yet.',
    noVessels: 'Add a vessel first: each vessel gets its folder tree from the document template.',
    documents_one: '{{count}} document',
    documents_other: '{{count}} documents',
    badges: {
      template: 'Template',
      officeModified: 'Office modified',
      retired: 'Retired',
    },
    badgeHints: {
      template: 'Unchanged since it came from the template; template updates apply.',
      officeModified: 'Changed by the office; template updates no longer change this folder.',
      retired: 'Read-only; can be restored.',
    },
    actions: {
      add: 'Add folder',
      addChild: 'Add subfolder',
      rename: 'Rename',
      move: 'Move',
      moveUp: 'Move up',
      moveDown: 'Move down',
      retire: 'Retire',
      restore: 'Restore',
      remove: 'Remove',
    },
    aria: {
      moveUp: 'Move up: {{name}}',
      moveDown: 'Move down: {{name}}',
      menu: 'Folder actions: {{name}}',
      restore: 'Restore: {{name}}',
    },
    form: {
      add: 'Add folder',
      addChild: 'Add subfolder to {{name}}',
      rename: 'Rename {{name}}',
      move: 'Move {{name}}',
      retire: 'Retire {{name}}',
      restore: 'Restore {{name}}',
      remove: 'Remove {{name}}',
      up: 'Move {{name}} up',
      down: 'Move {{name}} down',
      code: 'Code',
      nameEn: 'Name (English)',
      nameTr: 'Name (Turkish)',
      nameTrHint: 'Optional; the English name is shown when empty.',
      parent: 'New parent folder',
      root: 'Top level',
      applyTo: 'Apply to',
      thisVessel: 'This vessel ({{name}})',
      selectedVessels: 'Selected vessels',
      allVessels: 'All vessels',
      vessels: 'Vessels',
      vesselsRequired: 'Select at least one vessel.',
      required: 'Required.',
      tooLong: 'Too long.',
      invalid: 'Check the code and the names.',
      apply: 'Apply',
      cancel: 'Cancel',
      retireNote:
        'No document is moved or deleted. Folders that hold documents remain visible under “Retired folders”.',
      restoreNote: 'The folder and the subfolders retired with it become live again.',
      removeNote:
        'The vessel is still being set up, so a folder added by mistake can be removed. Only an empty folder added by the office can be removed; the change history is kept.',
      keyNote: 'The folder is found on each vessel by its permanent key.',
      impactVessel: 'Vessel',
      impactDocuments: 'Documents',
      impactAbsent: 'Not on this vessel',
      impactRetired: 'Already retired',
      impactLoading: 'Counting documents…',
    },
    result: {
      done_one: 'Applied to {{count}} vessel.',
      done_other: 'Applied to {{count}} vessels.',
      rejected:
        'No vessel was changed. Fix the problem below or choose other vessels, then try again.',
      vessel: 'Vessel',
      status: 'Result',
      applied: 'Applied',
      unchanged: 'No change',
      not_present: 'Not present',
      error: 'Rejected',
      close: 'Close',
    },
  },
  distribution: {
    title: 'Distribution',
    subtitle: 'Company documents: revision on board, download and acknowledgement on every vessel.',
    caption: 'Distribution of {{section}} by vessel',
    back: 'Back to {{section}}',
    document: 'Document',
    issues: 'Issues',
    onlyIssues: 'Only rows with issues',
    status: {
      current: 'On board',
      downloading: 'Downloading',
      stale: 'Old revision',
      deferred: 'Deferred: quota or disk',
      upcoming: 'Next revision on board',
      missing: 'Not received',
      failed: 'Download failed',
      out_of_scope: 'Not distributed',
      no_current: 'No current revision',
    },
    acknowledged: '{{name}} · {{date}}',
    late: 'Late acknowledgement',
    notAcknowledged: 'Not acknowledged',
    lastAcknowledged: 'last: Rev. {{revision}}',
    withdrawal: 'Withdrawal of Rev. {{revision}}',
    reads: 'Read {{confirmed}}/{{expected}}',
    lastPacket: 'Last packet {{date}}',
    noPacket: 'No packet yet',
    empty: 'No documents in this section yet.',
    noVessels: 'No vessels yet.',
    showing: 'Showing {{from}}–{{to}} of {{total}}',
  },
  revisionChoice: {
    original: 'Original (Rev. {{no}})',
    rev: 'Rev. {{no}}',
  },
  pending: {
    title: 'Documents awaiting approval',
    awaitsApproval: '{{document}} · Rev. {{revision}} awaits approval',
    awaitsPublishing: '{{document}} · Rev. {{revision}} awaits publishing',
  },
};

export const documentsOfficeTr: typeof documentsOfficeEn = {
  storage: {
    title: 'Doküman dosyaları',
    quota: 'Depolama kotası',
    used: '{{used}} / {{quota}}',
    disk: 'Gemi sunucusu diski',
    free: '{{free}} boş',
    levels: { ok: 'Yeterli', low: 'Az', critical: 'Kritik' },
    waiting: 'Bekleyen',
    failed: 'Başarısız',
    bulk: 'Toplu indirme',
    running: 'Sürüyor',
    paused: '{{date}} tarihine kadar duraklatıldı ({{by}})',
    pause: 'Toplu indirmeyi duraklat',
    pauseFor_one: '{{count}} gün',
    pauseFor_other: '{{count}} gün',
    resume: 'İndirmeyi sürdür',
    hint: 'Formlar ve prosedürler önce iner; büyük el kitapları toplu indirme penceresini bekler.',
    import: 'Klasörden içe aktar',
    importTitle: 'Doküman dosyalarının bulunduğu klasörü seçin (USB ya da yedek disk)',
    importHint: 'Dosyalar içeriğe göre eşleşir; adları önemli değildir.',
    progress: {
      listing: 'Dosyalar listeleniyor: {{files}}',
      scanning: 'Dosyalar denetleniyor: {{files}} / {{total}}',
      uploading: 'Dosyalar kopyalanıyor: {{done}} / {{total}}',
      starting: 'Klasör açılıyor…',
    },
    importDone_one: '{{count}} dosya içe aktarıldı, {{failed}} başarısız',
    importDone_other: '{{count}} dosya içe aktarıldı, {{failed}} başarısız',
    importNothing: 'Klasörde eksik dosya bulunmadı.',
    importFailed: 'İçe aktarma başarısız.',
  },
  read: {
    title: 'Okudum ve anladım',
    body: '{{revision}} için kişisel okuma beyanınız gerekiyor.',
    confirm: 'Okudum',
    dialogTitle: 'Okuma beyanı',
    dialogBody: '{{document}} {{revision}} revizyonunu okudum ve anladım.',
    password: 'Parola',
    passwordHint: 'İmzalamak için parolanızı girin.',
    cancel: 'Vazgeç',
    done: 'Okuma beyanı kaydedildi.',
  },
  vesselFiles: {
    loading: 'Geminin dosya ayarları yükleniyor…',
    loadError: 'Geminin dosya ayarları okunamadı.',
    readOnly: 'Bu ayarları değiştirmek Ayarlar yetkisi ister.',
    open: '{{vessel}} dosyaları',
    title: '{{vessel}} dosyaları',
    used: '{{used}} / {{quota}}',
    deferred: '{{count}} ertelendi',
    failed: '{{count}} başarısız',
    paused: 'Gemide duraklatıldı',
    disk: { ok: 'Disk yeterli', low: 'Disk az', critical: 'Disk kritik' },
    tabs: { settings: 'İndirme ayarları', export: 'USB için dışa aktar' },
    quota: 'Depolama kotası (GB)',
    threshold: 'Hemen inen dosya sınırı (MB)',
    thresholdHint:
      'Formlar, prosedürler ve güncellemeler dışında büyük dosyalar toplu pencereyi bekler.',
    mode: 'Toplu indirme',
    modes: { window: 'Zaman penceresinde', always: 'Her zaman', manual: 'Yalnız istenince' },
    windowStart: 'Pencere başlangıcı',
    windowEnd: 'Pencere bitişi',
    windowHint: 'Gemi yerel saati.',
    cap: 'Aylık toplu sınır (GB)',
    capHint: '0 sınırı kapatır.',
    rate: 'Hız sınırı (kbps)',
    rateHint: '0 sınırsız demektir.',
    save: 'Kaydet',
    cancel: 'Vazgeç',
    saved: 'İndirme ayarları kaydedildi.',
    invalid:
      "Değerleri denetleyin: kota ve sınır 0'dan büyük, pencere başlangıcı ve bitişi farklı.",
    exportBody:
      '{{vessel}} gemisinin henüz doğrulamadığı dosyaları bir klasöre (ör. USB disk) kopyalar. Gemide Settings › Doküman dosyaları › Klasörden içe aktar kullanılır.',
    exportCount_one: '{{count}} dosya',
    exportCount_other: '{{count}} dosya',
    exportNone: 'Gemide bütün dosyalar var. Dışa aktarılacak dosya yok.',
    exportStart: 'Klasör seç ve dışa aktar',
    exportProgress: '{{done}} / {{total}} dosya',
    exportDone: 'Dışa aktarma bitti.',
    unsupported: 'Chrome ya da Edge gerekir (File System Access).',
  },
  page: { first: 'İlk sayfa', previous: 'Önceki sayfa', next: 'Sonraki sayfa', last: 'Son sayfa' },
  filter: {
    label: 'Göster',
    all: 'Tümü',
    pending: 'Onay bekleyen ({{count}})',
    pendingEmpty: 'Onay bekleyen doküman yok.',
  },
  workflow: {
    title: 'Onay akışı',
    revision: 'Rev. {{revision}}',
    preparedBy: 'Hazırlayan {{name}}, {{date}}',
    events: {
      submitted: 'Onaya gönderen {{name}}, {{date}}',
      returned: 'İade eden {{name}}, {{date}}',
      approved: 'Onaylayan {{name}}, {{date}}',
      published: 'Yayınlayan {{name}}, {{date}}',
      rescheduled: 'Erteleyen {{name}}, {{date}}',
    },
    reason: 'Gerekçe: {{reason}}',
    waiting: {
      draft: 'Taslak – henüz onaya gönderilmedi.',
      pending_approval: 'Onaylayanın kararını bekliyor.',
      approved: 'Onaylandı – yayına hazır.',
      scheduled: '{{date}} tarihinde yürürlüğe girer.',
      published: 'Yürürlükteki revizyon.',
    },
    waitingVessel: 'Dokümanı artık yalnız şirket yayınlar. Bu revizyon yayınlanmayacak.',
    actions: {
      submit: 'Onaya gönder',
      return: 'İade et',
      approve: 'Onayla',
      publish: 'Gemilere yayınla',
      withdraw: 'Geri çek',
      discard: 'Vazgeç',
      reschedule: 'Yeniden zamanla',
      cancel: 'Yayını iptal et',
    },
    dialog: {
      titles: {
        submit: 'Onaya gönder',
        return: 'Yükleyene iade et',
        approve: 'Revizyonu onayla',
        publish: 'Revizyonu yayınla',
        withdraw: 'Revizyonu geri çek',
        discard: 'Taslaktan vazgeç',
        reschedule: 'Revizyonu yeniden zamanla',
        cancel: 'Yayını iptal et',
      },
      bodies: {
        submit: '{{document}} Rev. {{revision}} onaylayana gider. Dosya artık değiştirilemez.',
        return:
          '{{document}} Rev. {{revision}} taslağa döner. Yükleyen ondan vazgeçip düzeltilmiş taslağı yükler.',
        approve: '{{document}} Rev. {{revision}} revizyonunu gösterilen içerikle onaylıyorum.',
        publish:
          '{{document}} Rev. {{revision}} kapsamdaki bütün gemilerde yürürlüğe girer. Önceki revizyon yürürlükten kalkar.',
        withdraw:
          '{{document}} Rev. {{revision}} geri çekilir. Gemiler basılı kopyaları kaldırıp teyit eder.',
        discard: '{{document}} Rev. {{revision}} taslağı kapanır. Geçmişte kalır.',
        reschedule: '{{document}} Rev. {{revision}} yeni tarihte yürürlüğe girer.',
        cancel:
          '{{document}} Rev. {{revision}} için zamanlanmış yayın iptal edilir. Yürürlükteki revizyon geçerli kalır.',
      },
      withdrawWarning:
        'Kapsamdaki gemiler yürürlükte revizyonsuz kalır. Hatalı revizyonu düzeltmek için düzeltilmiş revizyonu yayınlayın.',
      withdrawVessels_one:
        '{{count}} gemi yürürlükte revizyonsuz kalır. Hatalı revizyonu düzeltmek için düzeltilmiş revizyonu yayınlayın.',
      withdrawVessels_other:
        '{{count}} gemi yürürlükte revizyonsuz kalır. Hatalı revizyonu düzeltmek için düzeltilmiş revizyonu yayınlayın.',
      reason: 'Gerekçe',
      reasonHint: 'Zorunlu. Geçmişe yazılır.',
      effectiveOn: 'Yürürlük tarihi',
      effectiveHint:
        'Hemen yürürlüğe girmesi için boş bırakın. Gemi yerel tarihi; en erken {{min}}, en geç {{max}}.',
      password: 'Parola',
      passwordHint: 'İmzalamak için parolanızı girin.',
      cancel: 'Vazgeç',
    },
    done: {
      submit: 'Rev. {{revision}} onaya gönderildi.',
      return: 'Rev. {{revision}} yükleyene iade edildi.',
      approve: 'Rev. {{revision}} onaylandı.',
      publish: 'Rev. {{revision}} yayınlandı.',
      withdraw: 'Rev. {{revision}} geri çekildi.',
      discard: 'Rev. {{revision}} taslağından vazgeçildi.',
      reschedule: 'Rev. {{revision}} yeniden zamanlandı.',
      cancel: 'Rev. {{revision}} yayını iptal edildi.',
    },
  },
  drawer: {
    newTitle: 'Yeni Doküman',
    fillHint:
      'Bu, kütüphaneye yeni form ekler; formu doldurmaz. Bir formu doldurmak için bu paneli kapatın, formu seçin ve Formu doldur’u kullanın.',
    revisionTitle: 'Yeni revizyon',
    category: 'Kategori',
    docNo: 'Doküman No',
    title: 'Başlık',
    kind: 'Tür',
    description: 'Açıklama',
    revisionNo: 'Revizyon No',
    revisionNoHint: 'Şirketin revizyon numarası, ör. 2.0.',
    revisionPickHint:
      'Formun kâğıttaki revizyonunu seçin: Orijinal (Rev. 0), Rev. 1, Rev. 2… Sonraki revizyonlar buradan devam eder.',
    changeSummary: 'Değişiklik Özeti',
    changeSummaryHint: 'Bu revizyonda ne değişti. Gemide gösterilir.',
    file: 'Doküman dosyası',
    fileHint: 'Word, Excel, PDF, JPG ya da PNG; en çok 1 GB.',
    drop: 'Dosyayı buraya bırakın ya da',
    choose: 'Dosya seçin',
    replace: 'Dosyayı değiştir',
    attachments: 'Ekler',
    attachmentsHint: 'İsteğe bağlı destek dosyaları.',
    printCopy: 'Baskı kopyası (PDF)',
    printCopyHint:
      'İsteğe bağlı. Word ya da Excel formunu PDF olarak kaydedip buraya ekleyin: Print ve Print Form yaklaşık çizim yerine bu PDF’i aynen basar.',
    addAttachments: 'Dosya ekle',
    remove: '{{name}} kaldır',
    scope: 'Dağıtım',
    scopeHint:
      'Kapsamdaki gemilerin bütün personeli bu dokümanı görür. Bir geminin kendi el kitabı (ör. stabilite kitapçığı) için Seçili gemiler’i seçin.',
    scopeChangeWarning:
      'Dağıtım dokümanındır: değişiklik bu yeni revizyonun yayınında değil, şimdi yürürlükteki revizyon için geçerli olur. Dağıtımdan çıkan gemiler bir sonraki senkronda dokümanı görmez. Taslağın iadesi ya da vazgeçilmesi bunu geri almaz.',
    scopeChangeConfirm: 'Dağıtımın şimdi değişeceğini anlıyorum.',
    vessels: 'Gemiler',
    vesselsPlaceholder: 'Gemi seçin',
    save: 'Taslağı kaydet',
    cancel: 'Vazgeç',
    uploading: '{{name}} yükleniyor',
    saving: 'Taslak kaydediliyor…',
    saved: 'Rev. {{revision}} taslağı kaydedildi. Hazır olunca onaya gönderin.',
    resume: 'Yükleme kesildi. Kaldığı yerden sürmesi için yeniden kaydedin.',
    locked:
      'Taslak revizyon bu dosya, numara ve özetle ve eklendi olarak işaretli eklerle kaydedildi; bunlar artık değişmez. Kalan adımlar için yeniden kaydedin.',
    attached: 'Revizyona eklendi',
    large:
      'Büyük dosya ({{size}}). Bölümlere ayırmanız önerilir; gemide gece penceresini bekler ve 1 Mbps ile yaklaşık {{hours}} saatte iner.',
  },
  chain: {
    title: 'Onay zinciri',
    hint: 'Kaydı kimin hazırladığı ve sırayla kimin onayladığı. Değişiklik kaydettikten sonra gönderilen kayıtlara uygulanır.',
    default: 'Hazır kurallar (onay matrisi)',
    custom: 'Özel zincir',
    preparers: 'Hazırlayan',
    preparersHint: '1 ya da 2 rütbe; formu birlikte hazırlarlar. Biri gönderir.',
    review: '{{step}}. ön onay',
    removeReview: '{{step}}. ön onayı kaldır',
    addReview: 'Ön onay adımı ekle',
    master: 'Kaptan onayı',
    masterHint: 'Kaptan ön onaylardan sonra onaylar.',
    quorumAll: 'Hepsi imzalar',
    quorumAny: 'Biri yeter',
    summary: 'Zincir: {{chain}}',
    defaultSummary: 'Hazır kurallar: kaptan gemide onaylar.',
    officeNote: 'Ofis kayıtları görür. ISM formları ofis onayına gitmez.',
    edit: 'Onay zincirini düzenle',
    dialogTitle: 'Onay zinciri · {{document}}',
    appliesNext:
      'Yeni zincir bundan sonra gönderilen kayıtlara uygulanır. Onay bekleyen kayıtlar gönderildikleri zincirle sürer.',
    save: 'Zinciri kaydet',
    saved: 'Onay zinciri kaydedildi.',
  },
  validation: {
    REQUIRED: 'Zorunlu.',
    TOO_LONG: 'Çok uzun.',
    INVALID: 'Geçersiz değer.',
    SCOPE_VESSELS: 'En az bir gemi seçin.',
    DUPLICATE: 'Aynı seçim iki kez.',
    READ_ROLES: 'En az bir rol seçin.',
    FILE_REQUIRED: 'Doküman dosyasını seçin.',
    PRINT_COPY_MUST_BE_PDF: 'Baskı kopyası PDF olmalı.',
    PRINT_COPY_NOT_APPLICABLE: 'Baskı kopyası yalnız Word ya da Excel doküman dosyasına eklenir.',
    CONTROL_CHARACTER: 'Satır sonlarını ve kontrol karakterlerini kaldırın.',
    CHAIN_PREPARER_REQUIRED: 'En az bir hazırlayan rütbe seçin.',
    CHAIN_STEP_EMPTY: 'Her adım için bir rütbe seçin.',
    INVALID_APPROVAL_CHAIN: 'Onay zinciri geçersiz.',
    CHAIN_ROLE_REPEATED: 'Bir rütbe zincirde yalnız bir kez yer alır.',
    UNKNOWN_ROLE: 'Seçilen rollerden biri artık yok.',
    FORM_OFFICE_STEP_NOT_ALLOWED: 'ISM formları ofis onayına gitmez.',
    CHAIN_ONLY_FOR_FORMS: 'Onay zinciri yalnız formlarda seçilir.',
    SCOPE_CHANGE_UNCONFIRMED: 'Dağıtımın şimdi değişeceğini onaylayın.',
    OLD_OFFICE_FORMAT:
      'Word 97–2003 (.doc) ve Excel 97–2003 (.xls) dosyaları kabul edilmez. Dosyayı Word ya da Excel’de açıp .docx ya da .xlsx olarak kaydedin.',
    MACRO_FILE:
      'Makrolu dosyalar (.docm, .xlsm) kabul edilmez. Dosyayı makrosuz .docx ya da .xlsx olarak kaydedin.',
    UNSUPPORTED_FILE_TYPE:
      'Bu dosya türü kabul edilmez. Word (.docx), Excel (.xlsx), PDF, JPG ya da PNG kullanın.',
  },
  categories: {
    title: 'Form Kategorileri',
    subtitle: 'ISM Formları kütüphanesinin klasör ağacı.',
    caption: 'Kategori ağacı',
    documents_one: '{{count}} doküman',
    documents_other: '{{count}} doküman',
    readOnly:
      "Kategori ağacı Felixmate'dan (gemi şablonu) gelir. Belgeler bu kategorilerin altına yüklenir.",
    empty: 'Henüz kategori yok.',
    retired: 'Emekli klasörler',
    retiredHint:
      'Bu klasörler emekliye ayrıldı. Salt okunurdur; hâlâ belge taşıdıkları için görünürler.',
    other: 'Diğer şirket dokümanları',
    otherHint: 'Klasörü bu geminin ağacında olmayan şirket dokümanları.',
  },
  folders: {
    title: 'Form Kategorileri',
    subtitle:
      'Her geminin klasör ağacı. Değişiklik bu gemiye, seçili gemilere ya da bütün gemilere uygulanır.',
    manualsTitle: 'El Kitapları – Klasörleri yönet',
    manualsCrumb: 'Klasörleri yönet',
    manualsSubtitle:
      'Her geminin El Kitapları klasör ağacı. Değişiklik bu gemiye, seçili gemilere ya da bütün gemilere uygulanır.',
    manage: 'Klasörleri yönet',
    back: '{{section}} bölümüne dön',
    library: 'Kütüphane',
    company: 'Şirket kütüphanesi (şablon aynası)',
    companyHint:
      'Şirket kütüphanesi Felixmate şablonunun aynasıdır ve salt okunurdur. Gemi ağaçları gemi bazında düzenlenir.',
    template: 'Şablon: {{name}} v{{version}}',
    noTemplate: 'Bu gemiye henüz doküman şablonu uygulanmadı.',
    readOnly:
      'Klasör ağacını görebilirsiniz. Düzenlemek için “Doküman ağacı” yetkisi (documents.tree) gerekir.',
    caption: 'Klasör ağacı',
    retiredCaption: 'Emekli klasörler',
    retiredHint:
      'Emekli klasörler salt okunurdur. Belgeler yerinde kalır; gemide belge taşıyan emekli klasörler “Emekli klasörler” altında görünür.',
    empty: 'Bu geminin bu bölümde henüz klasörü yok.',
    noVessels: 'Önce gemi ekleyin: her gemi klasör ağacını doküman şablonundan alır.',
    documents_one: '{{count}} doküman',
    documents_other: '{{count}} doküman',
    badges: {
      template: 'Şablon',
      officeModified: 'Ofis değiştirdi',
      retired: 'Emekli',
    },
    badgeHints: {
      template: 'Şablondan geldiği gibi; şablon güncellemeleri uygulanır.',
      officeModified: 'Ofis değiştirdi; şablon güncellemeleri artık bu klasörü değiştirmez.',
      retired: 'Salt okunur; geri alınabilir.',
    },
    actions: {
      add: 'Klasör ekle',
      addChild: 'Alt klasör ekle',
      rename: 'Yeniden adlandır',
      move: 'Taşı',
      moveUp: 'Yukarı taşı',
      moveDown: 'Aşağı taşı',
      retire: 'Emekliye ayır',
      restore: 'Geri al',
      remove: 'Kaldır',
    },
    aria: {
      moveUp: 'Yukarı taşı: {{name}}',
      moveDown: 'Aşağı taşı: {{name}}',
      menu: 'Klasör işlemleri: {{name}}',
      restore: 'Geri al: {{name}}',
    },
    form: {
      add: 'Klasör ekle',
      addChild: '{{name}} altına klasör ekle',
      rename: '{{name}} klasörünü yeniden adlandır',
      move: '{{name}} klasörünü taşı',
      retire: '{{name}} klasörünü emekliye ayır',
      restore: '{{name}} klasörünü geri al',
      remove: '{{name}} klasörünü kaldır',
      up: '{{name}} klasörünü yukarı taşı',
      down: '{{name}} klasörünü aşağı taşı',
      code: 'Kod',
      nameEn: 'Ad (İngilizce)',
      nameTr: 'Ad (Türkçe)',
      nameTrHint: 'İsteğe bağlı; boşsa İngilizce ad gösterilir.',
      parent: 'Yeni üst klasör',
      root: 'En üst düzey',
      applyTo: 'Uygula',
      thisVessel: 'Bu gemi ({{name}})',
      selectedVessels: 'Seçili gemiler',
      allVessels: 'Bütün gemiler',
      vessels: 'Gemiler',
      vesselsRequired: 'En az bir gemi seçin.',
      required: 'Zorunlu.',
      tooLong: 'Çok uzun.',
      invalid: 'Kodu ve adları kontrol edin.',
      apply: 'Uygula',
      cancel: 'Vazgeç',
      retireNote:
        'Hiçbir belge taşınmaz ya da silinmez. Belge taşıyan klasörler “Emekli klasörler” altında görünmeye devam eder.',
      restoreNote: 'Klasör ve onunla birlikte emekliye ayrılan alt klasörler yeniden canlı olur.',
      removeNote:
        'Gemi henüz kurulumda; yanlışlıkla açılan klasör kaldırılabilir. Yalnız ofisin açtığı boş klasör kaldırılır; değişiklik geçmişi kalır.',
      keyNote: 'Klasör her gemide kalıcı anahtarıyla bulunur.',
      impactVessel: 'Gemi',
      impactDocuments: 'Doküman',
      impactAbsent: 'Bu gemide yok',
      impactRetired: 'Zaten emekli',
      impactLoading: 'Dokümanlar sayılıyor…',
    },
    result: {
      done_one: '{{count}} gemiye uygulandı.',
      done_other: '{{count}} gemiye uygulandı.',
      rejected:
        'Hiçbir gemi değişmedi. Aşağıdaki sorunu giderin ya da başka gemiler seçip yeniden deneyin.',
      vessel: 'Gemi',
      status: 'Sonuç',
      applied: 'Uygulandı',
      unchanged: 'Değişiklik yok',
      not_present: 'Gemide yok',
      error: 'Reddedildi',
      close: 'Kapat',
    },
  },
  distribution: {
    title: 'Dağıtım',
    subtitle: 'Şirket dokümanları: her gemideki revizyon, indirme ve teyit.',
    caption: '{{section}} dağıtımı, gemi başına',
    back: '{{section}} ekranına dön',
    document: 'Doküman',
    issues: 'Sorunlar',
    onlyIssues: 'Yalnız sorunlu satırlar',
    status: {
      current: 'Gemide',
      downloading: 'İniyor',
      stale: 'Eski revizyon',
      deferred: 'Ertelendi: kota ya da disk',
      upcoming: 'Sonraki revizyon gemide',
      missing: 'Ulaşmadı',
      failed: 'İndirilemedi',
      out_of_scope: 'Dağıtılmıyor',
      no_current: 'Yürürlükte revizyon yok',
    },
    acknowledged: '{{name}} · {{date}}',
    late: 'Geç teyit',
    notAcknowledged: 'Teyit edilmedi',
    lastAcknowledged: 'son teyit: Rev. {{revision}}',
    withdrawal: 'Rev. {{revision}} geri çekmesi',
    reads: 'Okuma {{confirmed}}/{{expected}}',
    lastPacket: 'Son paket {{date}}',
    noPacket: 'Henüz paket yok',
    empty: 'Bu bölümde henüz doküman yok.',
    noVessels: 'Henüz gemi yok.',
    showing: '{{total}} kayıttan {{from}}–{{to}}',
  },
  revisionChoice: {
    original: 'Orijinal (Rev. {{no}})',
    rev: 'Rev. {{no}}',
  },
  pending: {
    title: 'Onay bekleyen dokümanlar',
    awaitsApproval: '{{document}} · Rev. {{revision}} onay bekliyor',
    awaitsPublishing: '{{document}} · Rev. {{revision}} yayın bekliyor',
  },
};
